import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { ROADS_UPDATED_KEY } from '../lib/logisticsUi';
import './DashboardPages.css';

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'slow', label: 'Slow' },
  { value: 'flooded', label: 'Flooded' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'seasonal_closed', label: 'Seasonal closed' },
];

const ROAD_TYPES = [
  { value: 'paved', label: 'Paved' },
  { value: 'dirt', label: 'Dirt' },
  { value: 'mixed', label: 'Mixed' },
];

const EMPTY_FORM = {
  from_locality: '',
  to_locality: '',
  status: 'open',
  road_type: 'paved',
  note: '',
  valid_until: '',
};

function statusLabel(value) {
  return STATUSES.find((s) => s.value === value)?.label || value;
}

function RoadsPage() {
  const [roads, setRoads] = useState([]);
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [{ roads: rows }, areasData] = await Promise.all([
        api.getRoads(),
        api.getServiceAreas(),
      ]);
      setRoads(rows || []);
      setAreas((areasData.areas || []).filter((a) => a.active !== false));
    } catch (err) {
      setError(err.message);
      setRoads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await api.createRoad({
        from_locality: form.from_locality,
        to_locality: form.to_locality,
        status: form.status,
        road_type: form.road_type,
        note: form.note.trim() || undefined,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : undefined,
      });
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(ROADS_UPDATED_KEY, '1');
      }
      setForm(EMPTY_FORM);
      setSuccess(
        form.status === 'blocked' || form.status === 'flooded' || form.status === 'seasonal_closed'
          ? 'Road report saved. Graph invalidated and clusters will recompute shortly.'
          : 'Road condition saved. Graph updated for the next route preview.'
      );
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const expireRoad = async (road) => {
    try {
      setError(null);
      setSuccess(null);
      await api.updateRoad(road.condition_id, {
        valid_until: new Date().toISOString(),
        status: 'open',
      });
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(ROADS_UPDATED_KEY, '1');
      }
      setSuccess(`Expired report ${road.from_locality} → ${road.to_locality}.`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const setRoadStatus = async (road, status) => {
    try {
      setError(null);
      setSuccess(null);
      await api.updateRoad(road.condition_id, { status });
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(ROADS_UPDATED_KEY, '1');
      }
      setSuccess(`Updated ${road.from_locality} → ${road.to_locality} to ${statusLabel(status)}.`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const localityOptions = areas.map((a) => a.name).filter(Boolean);

  return (
    <div className="dashboard-page">
      <h1>Road conditions</h1>
      <p className="dashboard-subtitle">
        Report slow, flooded, or closed segments. Routing uses these until they expire.
      </p>

      <section className="entity-manual-panel" aria-labelledby="road-report-heading">
        <h2 id="road-report-heading" className="entity-manual-title">Report road</h2>
        <form className="entity-manual-form" onSubmit={handleSave}>
          <div className="entity-manual-fields">
            <div className="form-group">
              <label htmlFor="road-from">From locality *</label>
              <select
                id="road-from"
                value={form.from_locality}
                onChange={(e) => setForm({ ...form, from_locality: e.target.value })}
                required
              >
                <option value="">Select locality</option>
                {localityOptions.map((name) => (
                  <option key={`from-${name}`} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="road-to">To locality *</label>
              <select
                id="road-to"
                value={form.to_locality}
                onChange={(e) => setForm({ ...form, to_locality: e.target.value })}
                required
              >
                <option value="">Select locality</option>
                {localityOptions.map((name) => (
                  <option key={`to-${name}`} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="road-status">Status *</label>
              <select
                id="road-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="road-type">Road type *</label>
              <select
                id="road-type"
                value={form.road_type}
                onChange={(e) => setForm({ ...form, road_type: e.target.value })}
              >
                {ROAD_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="road-until">Valid until</label>
              <input
                id="road-until"
                type="datetime-local"
                value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="road-note">Note</label>
              <input
                id="road-note"
                type="text"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="e.g. bridge washed out near Galim"
              />
            </div>
          </div>
          <button type="submit" className="entity-add-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save road report'}
          </button>
        </form>
      </section>

      {success && <div className="orders-success">{success}</div>}
      {error && <div className="orders-error">{error}</div>}

      <h2 className="entity-list-heading">Current conditions</h2>
      {loading ? (
        <div className="orders-placeholder"><p>Loading road conditions…</p></div>
      ) : roads.length === 0 ? (
        <div className="orders-placeholder">
          <p>No active road reports.</p>
        </div>
      ) : (
        <div className="roads-table-wrap">
          <table className="roads-table">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Status</th>
                <th>Type</th>
                <th>Note</th>
                <th>Until</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roads.map((road) => (
                <tr key={road.condition_id}>
                  <td>{road.from_locality}</td>
                  <td>{road.to_locality}</td>
                  <td>
                    <span className={`road-status road-status-${road.status}`}>
                      {statusLabel(road.status)}
                    </span>
                  </td>
                  <td>{road.road_type}</td>
                  <td>{road.note || '—'}</td>
                  <td>
                    {road.valid_until
                      ? new Date(road.valid_until).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                      : 'Open-ended'}
                  </td>
                  <td>
                    <div className="area-row-actions">
                      {road.status !== 'blocked' && (
                        <button
                          type="button"
                          className="entity-btn entity-delete"
                          onClick={() => setRoadStatus(road, 'blocked')}
                        >
                          Block
                        </button>
                      )}
                      {road.status !== 'open' && (
                        <button
                          type="button"
                          className="entity-btn entity-edit"
                          onClick={() => setRoadStatus(road, 'open')}
                        >
                          Mark open
                        </button>
                      )}
                      <button
                        type="button"
                        className="entity-btn"
                        onClick={() => expireRoad(road)}
                      >
                        Expire
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default RoadsPage;
